import { RefObject, useCallback, useEffect, useState } from 'react';

/**
 * Отслеживает возможность горизонтального скролла у ref-элемента.
 * Returns: canScrollLeft / canScrollRight + recalc-функция для ручного вызова.
 *
 * Слушает 'scroll' (passive) + ResizeObserver на ширину контейнера —
 * этого достаточно чтобы корректно disable'ить pager-кнопки при resize
 * aside / fullscreen / смене viewport'а.
 *
 * recalcOn — массив значений, на изменение которых нужно пересчитать состояние
 * без подписки на scroll (например при смене tableData у TableDemand).
 */
export function useScrollState(
    ref: RefObject<HTMLElement | null>,
    recalcOn: unknown[] = [],
): { canScrollLeft: boolean; canScrollRight: boolean; recalc: () => void } {
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const recalc = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setCanScrollLeft(el.scrollLeft > 1);
        setCanScrollRight(max - el.scrollLeft > 1);
    }, [ref]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        recalc();
        el.addEventListener('scroll', recalc, { passive: true });
        const ro = new ResizeObserver(recalc);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', recalc);
            ro.disconnect();
        };
        // recalcOn — внешняя триггерящая зависимость
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recalc, ...recalcOn]);

    return { canScrollLeft, canScrollRight, recalc };
}
